export function useFreightProgress() {
  const { calculateTotalByReference, calculateCartTotal } = usePriceUtils()
  const { splitCartByTemperature } = useCartSplit()

  type ReferenceTag = 'OFFICE' | 'PHARMACY' | null

  /**
   * Helper for checking if cart contains any chilled products
   */
  function hasChilledItems(cart: any): boolean {
    return cart.items?.some((item: any) => item.temperatureType === 'CHILLED') ?? false
  }

  function getFreightAllowance(cart: any): number {
    const supplier = cart.supplier
    const costCalculationType = supplier?.costCalculationType || 'STANDARD'

    // Configuration B (COMBINED): Use combined freight allowance if cart has chilled items
    if (costCalculationType === 'COMBINED' && hasChilledItems(cart)) {
      const combined = supplier?.freightAllowanceCombined
      if (combined?.amount)
        return combined.amount
    }

    // Configuration A (STANDARD) or COMBINED with no chilled items
    const standard = supplier?.freightAllowance
    if (typeof standard === 'number')
      return standard
    return Number.POSITIVE_INFINITY
  }

  function getFreightCharge(cart: any): number {
    const supplier = cart.supplier
    const costCalculationType = supplier?.costCalculationType || 'STANDARD'
    const totalCost = calculateCartTotal(cart)
    const freightAllowance = getFreightAllowance(cart)

    // If total is above freight allowance, freight is free
    if (totalCost >= freightAllowance) {
      return 0
    }

    // Configuration B (COMBINED): Use combined freight charge if cart has chilled items
    if (costCalculationType === 'COMBINED' && hasChilledItems(cart)) {
      const combined = supplier?.freightChargeCombined
      if (combined?.amount)
        return combined.amount
    }

    // Configuration A (STANDARD) or COMBINED with no chilled items
    const standard = supplier?.freightCharge
    if (typeof standard === 'number')
      return standard
    return 0
  }

  function hasNegotiatedPrices(cart: any): boolean {
    return cart.items?.some((item: any) =>
      item.scalePrices?.some((sp: any) => sp.type === 'INDIVIDUAL' || sp.type === 'GPO'),
    ) ?? false
  }

  /**
   * True when every item in the cart has a negotiated (INDIVIDUAL/GPO)
   * scale price. Used to suppress UI (e.g. the stock-up checkbox) that only
   * makes sense for list-priced carts.
   */
  function hasOnlyNegotiatedPrices(cart: any): boolean {
    if (!cart.items?.length)
      return false
    return cart.items.every((item: any) =>
      item.scalePrices?.some((sp: any) => sp.type === 'INDIVIDUAL' || sp.type === 'GPO'),
    )
  }

  function calculateProgress(cart: any, referenceTag: ReferenceTag = null) {
    const totalCost = referenceTag
      ? calculateTotalByReference(cart, referenceTag)
      : calculateCartTotal(cart)
    const freightAllowance = getFreightAllowance(cart)
    const progress = (totalCost / freightAllowance) * 100
    const successThreshold = hasNegotiatedPrices(cart) ? 100 : 130
    return {
      value: progress,
      color: getProgressColor(progress, successThreshold),
      totalCost,
      freightAllowance,
    }
  }

  function getProgressColor(progress: number, successThreshold: number = 130) {
    if (progress >= successThreshold)
      return 'success'
    if (progress >= 100)
      return 'warning'
    return 'error'
  }

  function getRemainingToFreeFreight(cart: any) {
    const totalCost = calculateCartTotal(cart)
    const freightAllowance = getFreightAllowance(cart)
    return Math.max(0, freightAllowance - totalCost)
  }

  /**
   * Total freight cost for a cart (single source for Summary, Dialog, Cart).
   * SEPARATE: sum of chilled + ambient segment charges (only segments with items).
   * STANDARD / COMBINED: single getFreightCharge(cart).
   */
  function getTotalFreightCost(cart: any): number {
    const costCalculationType = cart.supplier?.costCalculationType || 'STANDARD'

    if (costCalculationType === 'SEPARATE') {
      const { chilledCart, ambientCart, chilledItems, ambientItems } = splitCartByTemperature(cart)
      let total = 0
      if (chilledItems.length > 0) total += getFreightCharge(chilledCart)
      if (ambientItems.length > 0) total += getFreightCharge(ambientCart)
      return total
    }

    return getFreightCharge(cart)
  }

  return {
    calculateProgress,
    getRemainingToFreeFreight,
    hasChilledItems,
    hasNegotiatedPrices,
    hasOnlyNegotiatedPrices,
    getFreightCharge,
    getTotalFreightCost,
  }
}
